import { checkUserExist } from '@/components/marketing-landing-page/action';
import { getPageId, gets2sCameFrom, gets2sSource } from '@/components/marketing-landing-page/utils';
import { useFirebaseRemoteConfig } from '@/contexts/Firebase/firebase-remote-config.context';
import { useDeviceDetails } from '@/hooks/useDeviceDetails';
import { getCampaignParams } from '@/lib/events/helper';
import { AppOtplessConfig } from '@/types/types';
import { fetchPlatform, getCurrEnvOtplessConfig, getOtpLength, isFromApp } from '@/utils/utils';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

export const TRUECALLER_RES = {
  200: 'Truecaller SDK initiated',
  400: 'Truecaller SDK not found',
};

export const OTPLessContext = createContext({
  enableOTPLess: false,
  shouldTriggerTruecaller: false,
  setEnableOTPLess: (enable: boolean) => {
    console.log('Enable OTPLess:', enable);
  },
  otplessSigninRef: null as any,
  error: '',
  loading: false,
  otpDestination: 'SMS',
  handleUserInteracted: () => {},
  isUserExists: false,
  initiateTruecallerAuth: async () => {
    return '';
  },
  resendOTPLessOTP: async (countryCode: string, phoneNumber: string, deliveryChannel?: string) => {
    return {
      userExists: false,
      otplessInitiated: false,
    } as any;
  },
  initiateAuth: async (countryCode: string, phoneNumber: string) => {
    return {
      userExists: false,
    } as any;
  },
  verifyOtplessAuth: async (otp: string) => {},
  setShouldTriggerTruecaller: (shouldTrigger: boolean) => {},
});

export const OTPLessProvider = ({ children }: { children: React.ReactNode }) => {
  const [enableOTPLess, setEnableOTPLess] = useState(false);
  const [shouldTriggerTruecaller, setShouldTriggerTruecaller] = useState(false);
  const [otplessScriptLoaded, setOtplessScriptLoaded] = useState(false);
  const otplessSigninRef = useRef<any>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isUserExists, setIsUserExists] = useState(false);
  const [oTPLessActiveSteps, setOTPLessActiveSteps] = useState({
    shouldTriggerTruecaller: true,
    userInteracted: false,
  });
  const [otpDestination, setOtpDestination] = useState<'SMS' | 'WHATSAPP' | 'VOICE_CALL'>('SMS');

  const firebaseRemoteConfig = useFirebaseRemoteConfig();

  const firebaseOtpLessConfig = firebaseRemoteConfig?.otp_less_config;

  const verifyOtplessAuth = async (otp: string): Promise<void> => {
    try {
      if (!otp) {
        setError('Please enter the OTP');
        return;
      }

      setLoading(true);
      setError('');

      if (otplessSigninRef.current) {
        const res = await otplessSigninRef.current.verify({
          channel: 'PHONE',
          phone: phoneNumber,
          otp,
          countryCode,
        });

        setLoading(false);
        if (res?.response?.errorMessage) {
          setError(res.response.errorMessage.split(': ')[1]);
          toast.error(res.response.errorMessage.split(': ')[1]);
        }
      } else {
        setError('SDK not initialized. Please try again later.');
        setLoading(false);
      }
    } catch (e) {
      setError('Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  const resendOTPLessOTP = async (
    countryCodeParam: string,
    phoneNumberParam: string,
    deliveryChannel?: string
  ) => {
    if (otplessSigninRef.current) {
      const otplessInitiate: any = {
        phone: phoneNumberParam,
        countryCode: countryCodeParam,
        channel: 'PHONE',
      };
      // if (countryCodeParam !== '+91') {
      //   otplessInitiate.deliveryChannel = 'WHATSAPP'
      //   setOtpDestination('WHATSAPP')
      // }
      if (deliveryChannel) {
        otplessInitiate.deliveryChannel = deliveryChannel;
        setOtpDestination('SMS');
      }
      try {
        setLoading(true);
        const otplessInitiateRes = await otplessSigninRef.current.initiate(otplessInitiate);
        setLoading(false);
        if (!otplessInitiateRes.success) {
          toast.error(otplessInitiateRes?.response?.errorMessage);
          return undefined;
        }
        return {
          userExists: isUserExists,
          otplessInitiated: true,
        };
      } catch (err: any) {
        toast.error(err?.response?.errorMessage);
        return undefined;
      }
    } else {
      setError('SDK not initialized. Please try again later.');
      setLoading(false);
      return undefined;
    }
  };

  // Move function above usage to fix no-use-before-define
  const initiateTruecallerAuth = async () => {
    let otplessTruecallerRes: any = TRUECALLER_RES[400];
    const getIsIOS = (): boolean => {
      const { userAgent, platform } = window.navigator;
      // Use array destructuring for platform and userAgent
      const [ua, pf] = [userAgent, platform];
      return (
        ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
          pf
        ) || ua.includes('Mac')
      );
    };
    const oauth = async (provider: any) => {
      try {
        const oauthRes = await otplessSigninRef.current.initiate({
          channel: 'OAUTH',
          channelType: provider,
        });
        otplessTruecallerRes = TRUECALLER_RES[200];
      } catch (oauthErr) {
        otplessTruecallerRes = TRUECALLER_RES[400];
      }
    };
    if (!getIsIOS()) {
      setOTPLessActiveSteps({
        ...oTPLessActiveSteps,
        shouldTriggerTruecaller: false,
      });
      await oauth('TRUE_CALLER');
    }
    return otplessTruecallerRes;
  };

  const initiateAuth = async (countryCodeParam: string, phoneNumberParam: string) => {
    setLoading(true);
    try {
      setCountryCode(countryCodeParam);
      setPhoneNumber(phoneNumberParam);
      const res: any = await checkUserExist(phoneNumberParam);
      const userExists = res && res.success;
      setIsUserExists(!!userExists);
      if (otplessSigninRef.current) {
        const otplessInitiate: any = {
          phone: phoneNumberParam,
          countryCode: countryCodeParam,
          channel: 'PHONE',
        };
        // if (countryCodeParam !== '+91') {
        //   otplessInitiate.deliveryChannel = 'WHATSAPP'
        // }
        try {
          const otplessRes = await otplessSigninRef.current.initiate(otplessInitiate);
          setLoading(false);
          if (!otplessRes.success) {
            toast.error(otplessRes?.response?.errorMessage);
            return { userExists, otplessInitiated: false };
          }
          return { userExists, otplessInitiated: true };
        } catch (err: any) {
          toast.error(err?.response?.errorMessage);
          return { userExists, otplessInitiated: false };
        }
      } else {
        setError('SDK not initialized. Please try again later.');
        setLoading(false);
        return { userExists, otplessInitiated: false };
      }
    } catch (err) {
      setError('Something went wrong. Please try again later.');
      setLoading(false);
      return { userExists: null, otplessInitiated: false };
    }
  };

  // Move handleUserInteracted above context value
  const handleUserInteracted = () => {
    setOTPLessActiveSteps({
      ...oTPLessActiveSteps,
      userInteracted: true,
    });
  };
  const searchParams = useSearchParams();
  const cameUsing = searchParams?.get('cameUsing') ?? '';
  const { deviceManufacturer, deviceModel, deviceOS } = useDeviceDetails();
  const deviceDetailsRef = useRef({ deviceManufacturer, deviceModel, deviceOS });

  useEffect(() => {
    deviceDetailsRef.current = { deviceManufacturer, deviceModel, deviceOS };
  }, [deviceManufacturer, deviceModel, deviceOS]);

  useEffect(() => {
    const getOtplessAppConfig = () => {
      const otplessAppConfigs: AppOtplessConfig[] =
        JSON.parse((firebaseOtpLessConfig as string) || '{}')?.configs || [];
      const otplessAppCurrEnvConfig = getCurrEnvOtplessConfig(otplessAppConfigs);
      return otplessAppCurrEnvConfig;
    };
    // Add the SDK script
    if (enableOTPLess && !otplessScriptLoaded) {
      let appId = process.env.NEXT_PUBLIC_CJR_OTPLESS_APP_ID || '';
      if (isFromApp()) {
        const appFirebaseConfig = getOtplessAppConfig();
        appId = appFirebaseConfig?.appId || '';
      }

      const script = document.createElement('script');
      script.id = 'otpless-sdk';
      script.src = 'https://otpless.com/v4.2/headless.js';
      script.setAttribute('data-appid', appId);
      script.async = true;

      // Append the script to the head
      document.head.appendChild(script);

      const otplessCallback = (eventCallback: any) => {
        // Handle the response from OTPless
        const ONETAP = async () => {
          const { response } = eventCallback;
          const { token, identities } = response;
          const channel = identities[0].channel;
          const identityValue = identities[0].identityValue;
          const phoneNumberObj = parsePhoneNumberFromString('+' + identityValue);

          const { utm_source, utm_medium, utm_campaign, gclid, fbclid } = getCampaignParams();
          if (channel === 'OAUTH') {
            const nationalNumber = phoneNumberObj?.nationalNumber;
            const res: any = await checkUserExist(nationalNumber as string);

            const userExists = res && res?.success;
            if (userExists) {
              localStorage.setItem('shouldRegister', 'false');
            } else {
              localStorage.setItem('shouldRegister', 'true');
            }
          }
          localStorage.setItem('OTP_VERIFICATION_CHANNEL', channel);

          let selectedCohort = '';
          try {
            if (typeof window !== 'undefined') {
              selectedCohort = localStorage.getItem('selectedCohortId') || '';
            }
          } catch (error) {
            console.warn('OTPLess: Error retrieving selected cohort:', error);
          }

          const loginEventMetadata = {
            came_from: gets2sCameFrom(),
            login_mode: 'otpless',
            page_id: getPageId() || '',
            platform: isFromApp() ? fetchPlatform() : 'web',
            selected_cohort: selectedCohort,
            source: gets2sSource(),
            view: 'otpless_view',
            gclid: gclid || '',
            fbclid: fbclid || '',
            source_link: typeof window !== 'undefined' ? window.location.href : '',
            device_manufacturer: deviceDetailsRef.current.deviceManufacturer || '',
            device_model: deviceDetailsRef.current.deviceModel || '',
            device_os: deviceDetailsRef.current.deviceOS || '',
          };

          window.PWWebSDK.apiClient
            .post<any>(
              `${process.env.NEXT_PUBLIC_API_BASE_URL_AUTH}/v3/oauth/public/otpless-verify-token`,
              {
                token,
                organizationId: window.PWWebSDK.config?.organizationId,
                firstName: '',
                utm_source,
                utm_medium,
                utm_campaign,
                device: deviceDetailsRef.current.deviceManufacturer,
                pageType: '',
                mlpName: '',
                mlpType: '',
                loginEventMetadata,
              }
            )
            .then((tokenResponse: any) => {
              window.PWWebSDK.setTokenContext = {
                token: tokenResponse.data.access_token,
                refresh_token: tokenResponse.data.refresh_token,
                randomId: window.PWWebSDK.randomId,
                expires_in: tokenResponse.data.expires_in,
              };
              window.PWWebSDK.setUser = tokenResponse.data.user;

              const customEvent = new CustomEvent('otpless:auth-success', {
                detail: {
                  user: tokenResponse.data.user,
                },
              });
              document.dispatchEvent(customEvent);
            });
          //   handleSuccessfulAuth(response)
        };

        // OTP auto-read
        const OTP_AUTO_READ = () => {
          const {
            response: { otp },
          } = eventCallback;

          // Auto-submit the OTP if needed
          if (otp && otp.length >= getOtpLength()) {
            // verifyOTP(otp)
            const customEvent = new CustomEvent('otpless:otp-auto-read', {
              detail: {
                otp,
              },
            });
            document.dispatchEvent(customEvent);
          }
        };

        const FAILED = () => {
          const { response } = eventCallback;
          console.error('Authentication failed:', response);
          setError('Authentication failed. Please try again.');
          setLoading(false);
        };

        const FALLBACK_TRIGGERED = () => {
          const { response } = eventCallback;

          // Handle fallback - OTP is now being sent to WhatsApp after SMS failed
          if (response && response.deliveryChannel === 'WHATSAPP') {
            setOtpDestination('WHATSAPP');
          } else if (response && response.deliveryChannel === 'VOICE_CALL') {
            setOtpDestination('VOICE_CALL');
          }
        };

        const EVENTS_MAP: any = {
          ONETAP,
          OTP_AUTO_READ,
          FAILED,
          FALLBACK_TRIGGERED,
        };

        if ('responseType' in eventCallback && EVENTS_MAP[eventCallback.responseType]) {
          EVENTS_MAP[eventCallback.responseType]();
        }
      };

      // Initialize after script loads
      script.onload = () => {
        if ((window as any).OTPless) {
          otplessSigninRef.current = new (window as any).OTPless((response: any) => {
            otplessCallback(response);
          });
        }
      };
    }

    return () => {};
  }, [enableOTPLess, firebaseOtpLessConfig]);

  // Wrap context value in useMemo to avoid unnecessary re-renders
  const contextValue = useMemo(() => {
    return {
      enableOTPLess,
      setEnableOTPLess,
      otplessSigninRef,
      isUserExists,
      error,
      loading,
      verifyOtplessAuth,
      resendOTPLessOTP,
      initiateAuth,
      otpDestination,
      handleUserInteracted,
      initiateTruecallerAuth,
      shouldTriggerTruecaller,
      setShouldTriggerTruecaller,
    };
  }, [
    enableOTPLess,
    setEnableOTPLess,
    otplessSigninRef,
    isUserExists,
    error,
    loading,
    verifyOtplessAuth,
    resendOTPLessOTP,
    initiateAuth,
    otpDestination,
    handleUserInteracted,
    initiateTruecallerAuth,
    shouldTriggerTruecaller,
    setShouldTriggerTruecaller,
  ]);

  return <OTPLessContext.Provider value={contextValue}>{children}</OTPLessContext.Provider>;
};

export const useOTPLess = () => {
  return useContext(OTPLessContext);
};
